import React, { useCallback, useEffect, useState } from 'react';
import { Filter, FunctionField, TextField, useDataProvider, useNotify } from 'react-admin';

import Popover from '@material-ui/core/Popover';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ArrowDropDownOutlinedIcon from '@material-ui/icons/ArrowDropDownOutlined';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import classnames from 'classnames';
import moment from 'moment';
import MomentUtils from '@date-io/moment';

import ActionButton from 'components/buttons/ActionButton';
import { Actions, Datagrid, List } from 'components/list';
import { ActionField } from 'components/fields';
import SearchInput from 'components/list/SearchInput';

import NameField from '../NameField';
import DeleteRewardBenefitField from './DeleteRewardBenefitField';
import EditBenefitActionField from './EditBenefitActionField';
import AddBenefitButton from './AddBenefitButton';
import SwitchField from './SwitchField';
import { RewardBenefitDto, RewardLevelDto } from '../../types';

const useStyles = makeStyles((theme) => ({
  form: {
    justifyContent: 'flex-end',
    width: '100%',
    '& .filter-field': {
      width: '50%',
    },
  },
  datePickerButton: {
    marginTop: theme.spacing(2),
    minWidth: 220,
    '& .MuiButton-label': {
      justifyContent: 'space-between',
      width: '100%',
    },
  },
  datePickerPopover: {
    '& .MuiToolbar-root': {
      display: 'none',
    },
  },
  dateButtonError: {
    color: theme.palette.error.main,
  },
  datePickerInput: {
    marginTop: theme.spacing(2),
    '& .MuiPickersDay-daySelected': {
      color: theme.palette.common.white,
    },
  },
}));

const VALID_AT_DATE_FORMAT = 'YYYY-MM-DD';
const VALID_AT_DATE_TIME_FORMAT = 'YYYY-MM-DD[T]00:00:00.000[Z]';

const parseValidAt = (value) => {
  if (!value) {
    return undefined;
  }

  const parsedValue = moment(value, VALID_AT_DATE_FORMAT, true);
  if (!parsedValue.isValid()) {
    return undefined;
  }

  return parsedValue.format(VALID_AT_DATE_TIME_FORMAT);
};

const formatValidAt = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const parsedValue = moment(value);
  if (!parsedValue.isValid()) {
    return '';
  }

  return parsedValue.utc().format(VALID_AT_DATE_FORMAT);
};

const ValidAtFilterInput = ({ label, input: { value, onChange }, meta: { touched, error } }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (newValue) => {
    if (!newValue) {
      onChange(undefined);
      handleClose();
      return;
    }

    const formattedDate = parseValidAt(moment(newValue).format(VALID_AT_DATE_FORMAT));
    onChange(formattedDate);
    handleClose();
  };

  const formattedValue = formatValidAt(value);
  const selectedDate = formattedValue ? moment(formattedValue, VALID_AT_DATE_FORMAT) : null;
  const displayValue = selectedDate?.isValid() ? selectedDate.format('LL') : '';

  return (
    <>
      <Popover
        className={classes.datePickerPopover}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <DatePicker
            className={classes.datePickerInput}
            autoOk
            clearable
            views={['month', 'date']}
            value={selectedDate}
            onChange={handleDateChange}
            open={Boolean(anchorEl)}
            variant="static"
          />
        </MuiPickersUtilsProvider>
      </Popover>

      <ActionButton
        onClick={handleOpen}
        className={classnames(
          classes.datePickerButton,
          touched && !!error && classes.dateButtonError
        )}
        endIcon={<ArrowDropDownOutlinedIcon />}>
        {`${label}: ${displayValue || '--'}`}
      </ActionButton>
    </>
  );
};

const BenefitsFilter = (props) => {
  const classes = useStyles();

  return (
    <Filter classes={classes} {...props}>
      <ValidAtFilterInput source="validAt" label="Valid at" alwaysOn />
      <SearchInput label="Search" source="search" alwaysOn onlySearch loading={props.loading} />
    </Filter>
  );
};

const getValidAtFilterValue = (filterValues) => {
  const value = filterValues?.validAt;
  return typeof value === 'string' && value ? value : undefined;
};

const BenefitActions = (props) => {
  const validAt = getValidAtFilterValue(props?.filterValues);

  return (
    <Actions {...props}>
      <AddBenefitButton validAt={validAt} />
    </Actions>
  );
};

const BenefitsDatagrid = ({
  filterValues,
  benefitLevelsById,
  updating,
  onToggleLevel,
  onBenefitSaved,
  ...props
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [levels, setLevels] = useState<RewardLevelDto[]>([]);
  const validAt = getValidAtFilterValue(filterValues);

  useEffect(() => {
    let isMounted = true;

    const fetchLevels = async () => {
      try {
        const { data } = await dataProvider.getList<RewardLevelDto>('rewardsLevel', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'ASC' },
          filter: validAt ? { validAt } : {},
        });

        if (!isMounted) {
          return;
        }

        setLevels(Array.isArray(data) ? data : []);
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        const message = error?.message || 'Failed to load reward levels';
        notify(message, 'warning');
      }
    };

    fetchLevels();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, notify, validAt]);

  return (
    <Datagrid {...props}>
      <NameField source="title" imageSource="iconUrl" label="Perk" />
      <TextField source="systemType" label="System integration" sortable={false} />
      {levels.map((level) => (
        <FunctionField
          key={level.id ?? level.name}
          label={level.name}
          sortable={false}
          render={(record: RewardBenefitDto) => (
            <SwitchField
              benefit={
                record?.id && Object.prototype.hasOwnProperty.call(benefitLevelsById, record.id)
                  ? { ...record, levels: benefitLevelsById[record.id] }
                  : record
              }
              levelId={level.id}
              loading={updating[`${record.id}-${level.id}`]}
              onToggle={onToggleLevel}
            />
          )}
        />
      ))}
      <ActionField
        benefitLevelsById={benefitLevelsById}
        onBenefitSaved={onBenefitSaved}
        validAt={validAt}>
        <EditBenefitActionField />
        <DeleteRewardBenefitField />
      </ActionField>
    </Datagrid>
  );
};

const Benefits = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [updating, setUpdating] = useState<Record<string, boolean>>({});
  const [benefitLevelsById, setBenefitLevelsById] = useState<Record<string, string[]>>({});

  const handleBenefitSaved = useCallback((benefitId: string, levels: string[]) => {
    if (!benefitId) {
      return;
    }

    setBenefitLevelsById((prev) => ({
      ...prev,
      [benefitId]: (levels ?? []).filter((id): id is string => Boolean(id)),
    }));
  }, []);

  const handleToggleLevel = async (benefit: RewardBenefitDto, levelId?: RewardLevelDto['id']) => {
    if (!benefit?.id || !levelId) {
      return;
    }

    const benefitId = benefit.id;
    const key = `${benefitId}-${levelId}`;

    const hasOverride = Object.prototype.hasOwnProperty.call(benefitLevelsById, benefitId);
    const currentLevels = (
      (hasOverride ? benefitLevelsById[benefitId] : benefit.levels) ?? []
    ).filter((id): id is string => Boolean(id));
    const isActive = currentLevels.includes(levelId);
    const nextLevels = isActive
      ? currentLevels.filter((id) => id !== levelId)
      : Array.from(new Set([...currentLevels, levelId]));

    setBenefitLevelsById((prev) => ({ ...prev, [benefitId]: nextLevels }));
    setUpdating((prev) => ({ ...prev, [key]: true }));

    try {
      if (isActive) {
        await dataProvider.delete('rewardsUpdatePerkLevels', {
          levelId,
          benefitId,
        });
      } else {
        await dataProvider.post('rewardsUpdatePerkLevels', {
          levelId,
          benefitId,
          data: {},
        });
      }
    } catch (error: any) {
      setBenefitLevelsById((prev) => {
        if (!hasOverride) {
          const next = { ...prev };
          delete next[benefitId];
          return next;
        }

        return { ...prev, [benefitId]: currentLevels };
      });

      const message = error?.message || 'Failed to update perk levels';
      notify(message, 'warning');
    } finally {
      setUpdating((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <List
      {...props}
      resource="rewardsPerk"
      basePath="/rewardsSettings"
      fullWidth
      actions={<BenefitActions />}
      filters={<BenefitsFilter />}>
      <BenefitsDatagrid
        {...props}
        benefitLevelsById={benefitLevelsById}
        updating={updating}
        onToggleLevel={handleToggleLevel}
        onBenefitSaved={handleBenefitSaved}
      />
    </List>
  );
};

export default Benefits;
